import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import {
    FourSegmentsColumn,
    FourSegmentsTable,
    PreviewDdlRequest,
    PreviewDdlResponse,
    ValidateTableNameRequest,
    ValidateTableNameResponse
} from "../../../models/bridge/four-segments-table.model";
import { BridgeBaseService } from "./bridge-base.service";

/**
 * Service for the 4Bridge — 4Segments customer-table endpoints under
 * /v1/4bridge/4segments/. Exposes table catalog, name validation, and
 * DDL preview operations consumed by the task wizard and edit form.
 */
@Injectable({
    providedIn: 'root'
})
export class FourSegmentsTablesService extends BridgeBaseService<FourSegmentsTable> {

    constructor(httpClient: HttpClient) {
        super(httpClient, '4segments/tables');
    }

    /** GET /v1/4bridge/4segments/tables — list all tables tracked for the tenant. */
    public listTables(): Observable<FourSegmentsTable[]> {
        return this.httpClient
            .get<FourSegmentsTable[]>(`${this.baseUrl}/${this.endpoint}`)
            .pipe(catchError((e: HttpErrorResponse): Observable<never> => this.handleError(e)));
    }

    /** GET /v1/4bridge/4segments/tables/{name} — single-table detail. */
    public getTable(name: string): Observable<FourSegmentsTable> {
        return this.httpClient
            .get<FourSegmentsTable>(`${this.baseUrl}/${this.endpoint}/${encodeURIComponent(name)}`)
            .pipe(catchError((e: HttpErrorResponse): Observable<never> => this.handleError(e)));
    }

    /** POST /v1/4bridge/4segments/tables/validate-name — identifier rules + uniqueness. */
    public validateName(name: string): Observable<ValidateTableNameResponse> {
        const body: ValidateTableNameRequest = { name };
        return this.httpClient
            .post<ValidateTableNameResponse>(`${this.baseUrl}/${this.endpoint}/validate-name`, body)
            .pipe(catchError((e: HttpErrorResponse): Observable<never> => this.handleError(e)));
    }

    /** POST /v1/4bridge/4segments/tables/preview-ddl — generate (don't execute) the CREATE TABLE SQL. */
    public previewDdl(tableName: string, columns: FourSegmentsColumn[]): Observable<PreviewDdlResponse> {
        const body: PreviewDdlRequest = { columns, table_name: tableName };
        return this.httpClient
            .post<PreviewDdlResponse>(`${this.baseUrl}/${this.endpoint}/preview-ddl`, body)
            .pipe(catchError((e: HttpErrorResponse): Observable<never> => this.handleError(e)));
    }
}
